import { IconButton } from "$components/button/icon-button";
import { BinIcon, PrinterIcon } from "$components/icons";
import {
  type MenuItem,
  PopoverMenu,
} from "$components/popover-menu/popover-menu";
import type { Invoice as RxInvoice } from "$global-providers/rxdb-provider/collections/invoice";
import { toast } from "$global-providers/toast-provider";
import { useUser } from "$global-providers/user-provider";
import type { PriceList } from "$routes/patient/[:patientId]/_lib/invoices/components/InvoiceCard/types";
import {
  canPerformAction,
  type InvoiceAction,
} from "$routes/patient/[:patientId]/_lib/invoices-v2/_lib/can-perform-action";
import { DeleteInvoiceModalV2 } from "$routes/patient/[:patientId]/_lib/invoices-v2/modals/delete-invoice-modal-v2";
import { ExemptInvoiceModalV2 } from "$routes/patient/[:patientId]/_lib/invoices-v2/modals/exempt-invoice-modal-v2";
import { RefundInvoiceModalV2 } from "$routes/patient/[:patientId]/_lib/invoices-v2/modals/refund-invoice-modal-v2";
import { VoidInvoiceModalV2 } from "$routes/patient/[:patientId]/_lib/invoices-v2/modals/void-invoice-modal-v2";
import { WaiveInvoiceModalV2 } from "$routes/patient/[:patientId]/_lib/invoices-v2/modals/waive-invoice-modal-v2";
import { PrintableInvoiceV2 } from "$routes/patient/[:patientId]/_lib/invoices-v2/printable-invoice-v2";
import { PrintableLaserReceiptV2 } from "$routes/patient/[:patientId]/_lib/invoices-v2/printable-laser-receipt-v2";
import { useFetchPatientDobRxdb } from "$routes/patient/[:patientId]/_lib/invoices-v2/use-fetch-patient-dob-rxdb";
import { usePrintThermalReceiptRxdb } from "$routes/patient/[:patientId]/_lib/invoices-v2/use-print-thermal-receipt-rxdb";
import BlockIcon from "@material-ui/icons/Block";
import LocalAtmIcon from "@material-ui/icons/LocalAtm";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import RemoveCircleOutlineIcon from "@material-ui/icons/RemoveCircleOutline";
import UndoIcon from "@material-ui/icons/Undo";
import type { InvoiceStatus } from "elephant-web/gql";
import { type ReactNode, useEffect, useState } from "react";
import type { RxDocument } from "rxdb";
import { InvoiceFooterBar } from "./invoice-footer-bar";

type ModalName = "delete" | "void" | "waive" | "exempt" | "refund";
type PrintTarget = "invoice" | "receipt";

/**
 * Print and delete sit on the bar itself as the design shows; the money
 * corrections the design has no slot for (void, waive, exempt, refund) live
 * behind an overflow menu so they stay reachable.
 */
export const InvoiceCardActions: FC<{
  doc: RxDocument<RxInvoice> | null;
  patientId: string;
  invoiceStatus: InvoiceStatus;
  mode: "draft" | "finalised";
  priceList: PriceList;
  payerNames: string[];
  totalRefundableInPence: number;
  currency: string;
  primaryAction?: ReactNode;
  settledNote?: ReactNode;
}> = ({
  doc,
  patientId,
  invoiceStatus,
  mode,
  priceList,
  payerNames,
  totalRefundableInPence,
  currency,
  primaryAction,
  settledNote,
}) => {
  const { role } = useUser();
  const patientDob = useFetchPatientDobRxdb(patientId);
  const { havePrinter, printReceipt, printerError, fetchError, resetErrors } =
    usePrintThermalReceiptRxdb(doc?.id ?? "", priceList);

  const [openModal, setOpenModal] = useState<ModalName | null>(null);
  const [printTarget, setPrintTarget] = useState<PrintTarget | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLButtonElement | null>(null);

  const allows = (action: InvoiceAction) =>
    canPerformAction(role, action, invoiceStatus);

  // The hook reports failures through state, which only lands on a later
  // render — reading them straight after `await printReceipt()` would miss them.
  useEffect(() => {
    const error = printerError ?? fetchError;
    if (!error) return;
    toast.error(String(error));
    resetErrors();
  }, [fetchError, printerError, resetErrors]);

  const handlePrint = async () => {
    if (mode === "finalised" && (await havePrinter())) {
      await printReceipt();
      return;
    }
    setPrintTarget(mode === "finalised" ? "receipt" : "invoice");
  };

  const chooseFromMenu = (modal: ModalName) => () => {
    setMenuAnchor(null);
    setOpenModal(modal);
  };

  const menuItems: MenuItem[] = [
    ...(allows("waive")
      ? [
          {
            id: "invoicing.v2.card.action.waive",
            defaultMessage: "Waive payment",
            icon: <RemoveCircleOutlineIcon />,
            onClick: chooseFromMenu("waive"),
          },
        ]
      : []),
    ...(allows("exempt")
      ? [
          {
            id: "invoicing.v2.card.action.exempt",
            defaultMessage: "Exempt payment",
            icon: <LocalAtmIcon />,
            onClick: chooseFromMenu("exempt"),
          },
        ]
      : []),
    ...(allows("refund")
      ? [
          {
            id: "invoicing.v2.card.action.refund",
            defaultMessage: "Refund",
            icon: <UndoIcon />,
            onClick: chooseFromMenu("refund"),
          },
        ]
      : []),
    ...(allows("void")
      ? [
          {
            id: "invoicing.v2.card.action.void",
            defaultMessage: "Void invoice",
            icon: <BlockIcon />,
            onClick: chooseFromMenu("void"),
          },
        ]
      : []),
  ];

  return (
    <>
      <InvoiceFooterBar
        mode={mode}
        actions={
          <>
            {allows("print") && (
              <IconButton
                color="primary"
                aria-label={mode === "finalised" ? "Print receipt" : "Print"}
                disabled={!doc}
                onClick={handlePrint}
              >
                <PrinterIcon />
              </IconButton>
            )}
            {allows("delete") && (
              <IconButton
                color="error"
                aria-label="Delete invoice"
                disabled={!doc}
                onClick={() => setOpenModal("delete")}
              >
                <BinIcon fill="currentColor" />
              </IconButton>
            )}
            {menuItems.length > 0 && (
              <IconButton
                color="primary"
                aria-label="More invoice actions"
                onClick={(event) => setMenuAnchor(event.currentTarget)}
              >
                <MoreVertIcon />
              </IconButton>
            )}
          </>
        }
        primaryAction={primaryAction}
        settledNote={settledNote}
      />

      {menuAnchor && (
        <PopoverMenu
          anchorEl={menuAnchor}
          open
          onClose={() => setMenuAnchor(null)}
          primaryMenuItems={menuItems}
        />
      )}

      {doc && openModal === "delete" && (
        <DeleteInvoiceModalV2
          doc={doc}
          open
          onClose={() => setOpenModal(null)}
        />
      )}
      {doc && openModal === "void" && (
        <VoidInvoiceModalV2 doc={doc} open onClose={() => setOpenModal(null)} />
      )}
      {doc && openModal === "waive" && (
        <WaiveInvoiceModalV2
          doc={doc}
          open
          onClose={() => setOpenModal(null)}
        />
      )}
      {doc && openModal === "exempt" && (
        <ExemptInvoiceModalV2
          doc={doc}
          open
          onClose={() => setOpenModal(null)}
          patientDob={patientDob}
        />
      )}
      {doc && openModal === "refund" && (
        <RefundInvoiceModalV2
          doc={doc}
          open
          onClose={() => setOpenModal(null)}
          currency={currency}
          totalRefundableInPence={totalRefundableInPence}
        />
      )}

      {doc && printTarget === "invoice" && (
        <PrintableInvoiceV2
          syncInvoiceId={doc.id}
          priceList={priceList}
          payerNames={payerNames}
          onAfterPrint={() => setPrintTarget(null)}
        />
      )}
      {doc && printTarget === "receipt" && (
        <PrintableLaserReceiptV2
          syncInvoiceId={doc.id}
          priceList={priceList}
          onAfterPrint={() => setPrintTarget(null)}
        />
      )}
    </>
  );
};
